import AudioSource, { PlayOptions } from '@/webgl/audio/AudioSource'
import Signal from '@/webgl/core/Signal'

export default class Sound {
  disposed: boolean = false
  gain: GainNode
  node: AudioBufferSourceNode
  start: number = 0
  duration: number

  onEnded: Signal<void>

  startTime: number
  pausedTime: number

  constructor(readonly source: AudioSource) {
    let output = this.source.engine.master
    if (source.config.output) output = source.config.output

    this.gain = this.source.engine.context.createGain()
    this.gain.connect(output)

    this.node = this.source.engine.context.createBufferSource()
    this.node.buffer = this.source.config.buffer

    this.node.connect(this.gain)

    this.onEnded = new Signal()

    this.node.addEventListener('ended', this._onended)
  }

  play(opts: PlayOptions) {
    this.gain.gain.value = opts.volume ?? 1
    const when = this.source.engine.context.currentTime + (opts.delay ?? 0)

    if (opts.loop) {
      this.node.loop = true
      this.node.loopStart = this.start
      this.node.loopEnd = this.start + this.duration
    }

    this.startTime = this.source.engine.context.currentTime

    if (this.start !== undefined && this.duration !== undefined && !opts.loop) {
      this.node.start(when, this.start, this.duration)
    } else if (this.start !== undefined) {
      this.node.start(when, this.start)
    } else {
      this.node.start(when)
    }
  }

  stop() {
    this.onEnded.emit()
    this.dispose()
  }

  getCurrentTime() {
    return this.source.engine.context.currentTime - this.startTime
  }

  private _onended = () => {
    this.onEnded.emit()
    this.dispose()
  }

  dispose() {
    if (this.disposed) return
    this.disposed = true

    this.node.removeEventListener('ended', this._onended)
    this.gain.disconnect()
    this.node.disconnect()

    try {
      this.node.stop()
    } catch (e) {
      console.error(e)
    }

    this.gain = null
    this.node = null
    this.onEnded.release()
  }
}
